import { range } from 'lodash-es';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { micReadinessFacade } from '../../../configure/host-inputs-facade';
import { captureLogger as logger } from '../../../configure/logger-facade';
import { useAudioAnalyzer } from '../../../useAudioAnalyzer';
import { useAudioSystem } from '../../../useAudioSystem';
import { BarsBase } from './BarsBase';
import { getBarHeights } from './getBarHeights';

interface Props {
  isMic: boolean;
  isTab: boolean;
  isSilent?: boolean;
  className?: string;
  size?: 'default' | 'compact';
  barCount?: 3 | 5;
}

export const Bars = ({
  isMic,
  isTab,
  isSilent,
  className,
  size = 'default',
  barCount = 5,
}: Props) => {
  const barsRef = useRef<HTMLDivElement[]>([]);
  const { getMicAnalyzer, getTabAnalyzer, releaseMicAnalyzer, releaseTabAnalyzer } =
    useAudioAnalyzer();
  const { isReady, errors } = useAudioSystem();
  const audioReady = isReady && !errors.length;
  // Host wiring bridges mic readiness. The server snapshot stays false.
  const readyToRequestMic = useSyncExternalStore(
    micReadinessFacade.subscribe,
    micReadinessFacade.getSnapshot,
    () => false
  );
  const minHeight = size === 'compact' ? 3 : 7.5;
  const heightMultiplier = size === 'compact' ? 0.5 : 1;
  // With 3 bars, render the centre three of the 5 frequency bands so the
  // mid-tall-mid arc and per-band responsiveness are preserved.
  const levelOffset = barCount === 3 ? 1 : 0;

  useEffect(() => {
    let animationFrameId: number;
    let isActive = true;
    let micAnalyzerId: string | undefined;
    let tabAnalyzerId: string | undefined;

    const startAnalyzing = async () => {
      try {
        let micAnalyzer = null;
        let tabAnalyzer = null;
        let micFrequencyData = new Uint8Array();
        let tabFrequencyData = new Uint8Array();

        if (isMic) {
          const micResult = await getMicAnalyzer();
          micAnalyzerId = micResult.id;
          micAnalyzer = micResult.analyzer;

          micAnalyzer.fftSize = 512;
          micAnalyzer.minDecibels = -115;
          micAnalyzer.maxDecibels = -15;
          micAnalyzer.smoothingTimeConstant = 0.4;

          micFrequencyData = new Uint8Array(micAnalyzer.frequencyBinCount);
        }
        if (isTab) {
          const tabResult = await getTabAnalyzer();
          tabAnalyzerId = tabResult.id;
          tabAnalyzer = tabResult.analyzer;

          tabAnalyzer.fftSize = 512;
          tabAnalyzer.minDecibels = -95;
          tabAnalyzer.maxDecibels = 5;
          tabAnalyzer.smoothingTimeConstant = 0.4;

          tabFrequencyData = new Uint8Array(tabAnalyzer.frequencyBinCount);
        }

        const barHeights = Array(5).fill(5);

        const updateVisualization = () => {
          if (!isActive) return;

          try {
            if (micAnalyzer) micAnalyzer.getByteFrequencyData(micFrequencyData);
            if (tabAnalyzer) tabAnalyzer.getByteFrequencyData(tabFrequencyData);
            const maxBinCount = Math.max(
              micFrequencyData.length,
              tabFrequencyData.length
            );
            const combinedFrequencyData = new Uint8Array(maxBinCount);
            for (let i = 0; i < maxBinCount; i++) {
              const micFrequency = micFrequencyData[i] ?? 0;
              const tabFrequency = tabFrequencyData[i] ?? 0;
              let combinedFrequency = micFrequency + tabFrequency;
              if (combinedFrequency > 255) combinedFrequency = 255;
              combinedFrequencyData[i] = combinedFrequency;
            }
            const newHeights = getBarHeights(combinedFrequencyData);

            // Easing effect.
            for (const i of range(0, 5)) {
              barHeights[i] += (newHeights[i]! - barHeights[i]) * 0.2;
            }

            // Only update DOM if component is still mounted
            if (isActive && barsRef.current) {
              requestAnimationFrame(() => {
                if (!isActive) return;

                barsRef.current.forEach((bar, index) => {
                  if (!bar) return;
                  const height = Math.max(
                    barHeights[index + levelOffset] * heightMultiplier,
                    minHeight
                  );
                  bar.style.height = `${height}px`;
                });
              });
            }

            animationFrameId = requestAnimationFrame(updateVisualization);
          } catch (error) {
            logger.error('Audio visualization error:', error);
            cancelAnimationFrame(animationFrameId);
          }
        };

        updateVisualization();
      } catch (error) {
        logger.error('Failed to initialize audio analyzer:', error);
      }
    };

    if (readyToRequestMic) {
      void startAnalyzing();
    }

    return () => {
      isActive = false;
      cancelAnimationFrame(animationFrameId);
      if (micAnalyzerId) void releaseMicAnalyzer(micAnalyzerId);
      if (tabAnalyzerId) void releaseTabAnalyzer(tabAnalyzerId);
    };
  }, [
    getMicAnalyzer,
    getTabAnalyzer,
    isMic,
    isTab,
    readyToRequestMic,
    releaseMicAnalyzer,
    releaseTabAnalyzer,
    heightMultiplier,
    minHeight,
    levelOffset,
  ]);

  return (
    <BarsBase
      barsRef={barsRef}
      isSilent={isSilent}
      audioReady={audioReady}
      className={className}
      size={size}
      barCount={barCount}
    />
  );
};
